import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  DEFAULT_OG_IMAGE,
  SEO_PAGES,
  SITE_NAME,
  SITE_URL,
  canonicalPath,
  findSeoPage,
} from "../../helpers/seo";

const setMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const setLink = (rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const setJsonLd = (id: string, data: Record<string, unknown>) => {
  let element = document.getElementById(id) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
};

const Seo = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/tools/")) {
      return;
    }

    const path = canonicalPath(location.pathname);
    const page = findSeoPage(path);
    const canonicalUrl = `${SITE_URL}${path === "/" ? "/" : path}`;
    const title = `${page.title} | ${SITE_NAME}`;
    const isDuplicateOrUtility =
      location.pathname !== path ||
      location.pathname.startsWith("/start") ||
      location.search.includes("tool=") ||
      location.search.includes("search=");

    document.documentElement.lang = "en";
    document.title = title;

    setMeta('meta[name="description"]', "name", "description", page.description);
    setMeta('meta[name="keywords"]', "name", "keywords", page.keywords.join(", "));
    setMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      isDuplicateOrUtility
        ? "noindex, follow, max-image-preview:large"
        : "index, follow, max-image-preview:large"
    );
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", page.description);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    setMeta('meta[property="og:image"]', "property", "og:image", DEFAULT_OG_IMAGE);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", page.description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", DEFAULT_OG_IMAGE);

    setLink("canonical", canonicalUrl);
    setLink("sitemap", `${SITE_URL}/sitemap.xml`);

    setJsonLd("structured-data-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_OG_IMAGE,
      sameAs: ["https://www.instagram.com/toolsforprocreate/"],
    });

    setJsonLd("structured-data-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/?search={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });

    if (path === "/how-it-works") {
      setJsonLd("structured-data-page", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        name: page.title,
        description: page.description,
        url: canonicalUrl,
        mainEntity: [
          {
            "@type": "Question",
            name: "How do I install Procreate brushes?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Download the brush set, choose Procreate from the iPad share menu, then find the imported brush inside Procreate.",
            },
          },
          {
            "@type": "Question",
            name: "How do I use Procreate stickers and interior design assets?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Save the asset image, open Photos and Procreate side by side, then drag and drop the sticker or interior element into your canvas.",
            },
          },
          {
            "@type": "Question",
            name: "How do I add Tools for Procreate to my home screen?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Tap the iPad share button, choose Add to Home Screen, then confirm Add.",
            },
          },
        ],
      });
      return;
    }

    setJsonLd("structured-data-page", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: page.title,
      description: page.description,
      url: canonicalUrl,
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
      about: page.keywords.map((keyword) => ({ "@type": "Thing", name: keyword })),
    });
  }, [location.pathname, location.search]);

  return null;
};

export { Seo, SEO_PAGES };
